import React, { useState } from "react";
import Image from "next/image";

import { BASE_ASSETS_URL } from "@constants/constants";

const legals = [
  {
    name: "ESIGN Act",
    image: "esign.png",
    fullForm:
      "Electronic Signatures in Global and National Commerce Act (ESIGN)",
    description:
      "We ensure every electronic signature on our platform is legally binding by verifying the signer's identity and capturing their intent to sign.",
  },
  {
    name: "UECA Act",
    image: `ueca-act.png`,
    fullForm: "Uniform Electronic Communications Act (UECA)",
    description:
      "BoloSign's robust record-keeping and verification processes ensure that any electronic records related to environmental covenants are legally valid and enforceable.",
  },
  {
    name: "Federal Decree Law",
    image: `federal-decree-law.png`,
    fullForm: "Federal Decree-Law No. 46 of 2021 (UAE)",
    description:
      "We adhere to this law by implementing robust security measures and ensuring that all electronic transactions are legally binding and recognized by UAE authorities.",
  },
  {
    name: "UETA Act",
    image: "ueta.png",
    fullForm: "The Uniform Electronic Transactions Act (UETA)",
    description:
      "BoloSign ensures clear consent from all parties to conduct business electronically, and our platform maintains robust records to validate each transaction.",
  },
  {
    name: "ETL Act",
    image: `etl-act.png`,
    fullForm: "Electronic Transactions Law (ETL)",
    description:
      "BoloSign adheres to the specific ETLs of each country where we operate, ensuring that electronic signatures are legally valid and compliant with local regulations.",
  },
  {
    name: "HIPAA",
    image: "hipaa2.png",
    fullForm: "Health Insurance Portability and Accountability Act (HIPAA)",
    description:
      "We implement stringent administrative, physical, and technical safeguards to protect electronic protected health information (ePHI) ensuring data confidentiality, integrity, and availability. ",
  },
  {
    name: "PIPEDA Act",
    image: `pipeda-act.png`,
    fullForm:
      "Personal Information Protection and Electronic Documents Act (PIPEDA)",
    description:
      "Our platform adheres to PIPEDA guidelines, ensuring that your data is protected and only used for its intended purpose.",
  },
  {
    name: "EIDAS Act",
    image: "eidas.png",
    fullForm: "eIDAS (European Union)",
    description:
      "We comply with eIDAS by offering advanced electronic signatures that meet EU standards. Our platform ensures secure and legally binding transactions across all EU member states.",
  },
  {
    name: "SOC 2 Type II",
    image: "soc2.png",
    fullForm: "SOC 2 (Service Organization Control 2)",
    description:
      "We adhere to SOC 2 criteria by implementing robust security measures, ensuring system availability, and maintaining data integrity. Our platform undergoes regular SOC 2 audits to verify that our controls are effective and meet industry standards. ",
  },
  {
    name: "CCLA Act",
    image: `ccla-act.png`,
    fullForm: "Contract and Commercial Law Act 2017 (CCLA)",
    description:
      "We provide transparent privacy policies and give users control over their personal data. Our platform allows users to opt out of the sale of their personal information, in compliance with CCLA.",
  },
  {
    name: "ETA Act",
    image: `eta-act.png`,
    fullForm: "Electronic Transactions Act 1999 (ETA)",
    description:
      "We align with the ETA by ensuring that electronic signatures on our platform are legally binding and that all electronic records are securely stored and easily accessible.",
  },
  {
    name: "SOC Type 1",
    image: "soc1.png",
    fullForm: "SOC 1 (Service Organization Control 1)",
    description:
      "We undergo regular SOC 1 audits to demonstrate our commitment to secure and reliable financial reporting. Our internal controls are designed to protect client data and ensure the accuracy of financial transactions. ",
  },
  {
    name: "GDPR",
    image: "gdpr.png",
    fullForm: "General Data Protection Regulation (GDPR)",
    description:
      "BoloSign ensures lawful, fair, and transparent data processing by obtaining explicit consent from users before collecting their data.",
  },
  {
    name: "CCPA",
    image: "ccpa.png",
    fullForm: "California Consumer Privacy Act (CCPA)",
    description:
      "We provide a transparent privacy policy that informs users about their data rights under the CCPA. Our platform allows users to access and delete their personal information easily. ",
  },
  {
    name: "ISO 27001",
    image: "iso.png",
    fullForm: "ISO (International Organization for Standardization)",
    description:
      "We follow ISO 27001 guidelines to establish, implement, maintain, and continually improve our information security management system (ISMS). ",
  },
];

const Legal = ({
  bgColor = "bg-accent-blue",
  shadow = true,
  complianceGroupImage = true,
}) => {
  const [showLaws, setShowLaws] = useState(false);
  const [showLawDescription, setShowLawDescription] = useState(-1);

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-6">
        {complianceGroupImage && (
          <div className="shrink-0">
            <Image
              src={`${BASE_ASSETS_URL}/v2/ltd/legal-hero.png`}
              alt="Legal"
              width={120}
              height={120}
              className="w-[96px] sm:w-[120px] object-contain"
            />
          </div>
        )}

        <div className="sm:grow sm:w-0">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            How does BoloSign’s legal compliance compare to industry leaders
            like DocuSign?
          </h1>

          <p className="mt-5 text-gray-700 leading-relaxed">
            BoloSign fully complies with all major e-signature regulations,
            including the ESIGN Act, UETA, PIPEDA, UECA, ETA, and CCLA. Our
            platform adheres to the same rigorous legal standards as industry
            leaders, ensuring documents signed through BoloSign maintain full
            legal validity across jurisdictions.
          </p>

          {/* Laws Grid */}
          <div
            className={`mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 ${
              showLaws ? "grid" : "hidden sm:grid"
            }`}
          >
            {legals.map((legal) => (
              <div
                key={legal.name}
                className="relative group bg-surface border border-surface-dark p-4 cursor-default rounded-md hover:cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <Image
                    src={
                      legal.local
                        ? legal.image
                        : `${BASE_ASSETS_URL}/v2/security/${legal.image}`
                    }
                    alt={legal.name}
                    width={40}
                    height={40}
                    className="w-10 h-10 object-contain"
                  />
                  <span className="text-gray-900 font-medium">
                    {legal.name}
                  </span>
                </div>

                {/* Tooltip (Only on Hover) */}
                <div className="absolute z-20 top-full left-0 mt-2 w-full rounded-lg bg-surface-dark text-gray-900 p-4 text-sm hidden group-hover:block transition-opacity duration-300 shadow-md  ">
                  <p>
                    <span className="font-semibold">{legal.fullForm}:</span>{" "}
                    {legal.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile toggle (optional: keep or remove) */}
          <div className="text-center sm:hidden">
            <button
              onClick={() => setShowLaws((prev) => !prev)}
              className="mt-6 px-6 py-3 bg-black text-white font-medium rounded-md"
            >
              {showLaws ? "Hide All Laws" : "Show All Laws"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Legal;
